<script>
    import {createEventDispatcher} from 'svelte';
    import {Toast} from 'svelma';
    import {highLowPlayers} from '../store';
    import {HighLowHistoryEvent} from '../highlow.model';

    const dispatch = createEventDispatcher();

    export let lives;

    const initialHighTarget = 45;
    const initialLowTarget = 26;
    let highTarget = initialHighTarget;
    let lowTarget = initialLowTarget;
    let activePlayerIndex = 0;
    let playerScore;
    let gameFinished = false;
    let history = [];
    let scoreInputElement;

    function handleScore(score, failed = false) {
        if (score == null && !failed) {
            Toast.create({message: 'Enter a score first.', type: 'is-danger'});
            return;
        }

        if (!failed && (score < 0 || score > 180)) {
            Toast.create({message: 'Enter a score between 0 and 180.', type: 'is-danger'});
            return;
        }

        const activePlayer = $highLowPlayers[activePlayerIndex];
        const succeededHigh = !failed && score > highTarget;
        const succeededLow = !failed && score < lowTarget;

        history = [...history, new HighLowHistoryEvent(
            activePlayerIndex,
            score,
            !failed,
            highTarget,
            lowTarget,
            activePlayer.lives,
            activePlayer.lastScore,
            activePlayer.lastResult
        )];

        highLowPlayers.update(players => {
            const player = players[activePlayerIndex];
            player.lastScore = failed ? null : score;

            if (succeededHigh) {
                player.lastResult = 'high';
                highTarget = score;
            } else if (succeededLow) {
                player.lastResult = 'low';
                lowTarget = score;
            } else {
                player.lastResult = 'miss';
                player.lives -= 1;
                player.eliminated = player.lives === 0;
            }

            return [...players];
        });

        checkWinner();
        if (!gameFinished) {
            nextPlayer();
        }

        if (scoreInputElement) {
            scoreInputElement.focus();
        }
    }

    function checkWinner() {
        gameFinished = $highLowPlayers.filter(player => !player.eliminated).length === 1;
    }

    function nextPlayer() {
        do {
            activePlayerIndex = (activePlayerIndex + 1) % $highLowPlayers.length;
        } while ($highLowPlayers[activePlayerIndex].eliminated);

        playerScore = null;
        if (scoreInputElement) {
            scoreInputElement.focus();
        }
    }

    function undo() {
        const event = history[history.length - 1];
        if (!event) {
            return;
        }
        history = history.slice(0, -1);

        gameFinished = false;
        highTarget = event.previousHighTarget;
        lowTarget = event.previousLowTarget;
        activePlayerIndex = event.playerIndex;
        playerScore = event.score;

        highLowPlayers.update(players => {
            const player = players[event.playerIndex];
            player.lives = event.previousLives;
            player.eliminated = false;
            player.lastScore = event.previousLastScore;
            player.lastResult = event.previousLastResult;
            return [...players];
        });

        if (scoreInputElement) {
            scoreInputElement.focus();
        }
    }

    function reset() {
        highTarget = initialHighTarget;
        lowTarget = initialLowTarget;
        activePlayerIndex = 0;
        playerScore = null;
        gameFinished = false;
        history = [];
        dispatch('reset', {lives});
        if (scoreInputElement) {
            scoreInputElement.focus();
        }
    }

    function newGame() {
        dispatch('newGame');
    }

    function onEnterScore(e) {
        if ((e.key === 'Enter' || e.charCode === 13 || e.keyCode === 13) && !gameFinished) {
            e.preventDefault();
            handleScore(playerScore);
        }
    }

    function init(element) {
        scoreInputElement = element;
        element.focus();
    }

    function isPlayerActive(player) {
        return $highLowPlayers[activePlayerIndex]?.name === player?.name && !gameFinished;
    }

    function isPlayerWinner(player) {
        return gameFinished && !player.eliminated;
    }
</script>

<div class="highlow-screen w-full h-screen min-h-screen text-slate-900 font-sans antialiased flex flex-col p-3 sm:p-4 md:p-5 lg:p-6 box-border" style="background-color: #f0ece9;">
    <!-- BEGIN: TopControlsBar -->
    <header class="w-full max-w-[1600px] mx-auto bg-white rounded-2xl sm:rounded-3xl shadow-sm px-4 sm:px-6 py-3 mb-3 sm:mb-4 border border-black/5 flex flex-wrap items-center justify-between gap-4 shrink-0">
        <!-- Brand Group -->
        <div class="flex items-center gap-4 sm:gap-6 min-w-[200px]">
            <button class="inline-flex items-center transition-opacity hover:opacity-85 focus:outline-none" on:click={newGame} title="Yuma Home">
                <img alt="Yuma Logo" class="h-8 sm:h-9 w-auto object-contain" src="images/yuma.png" />
            </button>
            <div class="h-8 w-px bg-slate-200"></div>
        </div>

        <!-- Active Input Center Console -->
        <div class="flex items-center justify-center flex-1 max-w-xl mx-auto order-last md:order-none w-full md:w-auto">
            <div class="flex items-center gap-2 w-full max-w-xl bg-stone-100/80 p-1.5 rounded-2xl border border-slate-200 shadow-inner">
                <div class="relative flex-1 min-w-[180px] sm:min-w-[220px]">
                    <input
                        class="w-full h-11 bg-white text-slate-900 text-lg font-bold pl-4 pr-10 rounded-xl border border-slate-200 placeholder-slate-400 focus:border-[#21e467] focus:ring-2 focus:ring-[#21e467]/30 transition-all text-center tracking-wide outline-none disabled:opacity-50"
                        type="number"
                        min="0"
                        max="180"
                        placeholder="Score..."
                        bind:value={playerScore}
                        on:keypress={onEnterScore}
                        use:init
                        disabled={gameFinished}
                    />
                    <span class="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 font-bold select-none pointer-events-none">PTS</span>
                </div>
                <!-- Next Player button -->
                <button
                    class="h-11 inline-flex items-center justify-center gap-2 bg-[#EBA9FF] hover:bg-[#1bc658] active:scale-[0.98] text-[#005d46] font-bold px-4 sm:px-5 rounded-xl transition-all shadow-sm border border-emerald-500/20 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    type="button"
                    on:click={() => handleScore(playerScore)}
                    disabled={gameFinished}
                >
                    <span>Next player</span>
                    <span class="material-symbols-outlined text-[18px]">arrow_forward</span>
                </button>
                <!-- Failed button -->
                <button
                    class="h-11 inline-flex items-center justify-center gap-1.5 bg-rose-100 hover:bg-rose-200 active:scale-[0.98] text-rose-700 font-bold px-3.5 sm:px-4 rounded-xl transition-all border border-rose-300/60 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    type="button"
                    on:click={() => handleScore(null, true)}
                    disabled={gameFinished}
                    title="Missed / Failed target"
                >
                    <span class="material-symbols-outlined text-[18px]">close</span>
                    <span class="hidden sm:inline">Failed</span>
                </button>
            </div>
        </div>

        <!-- Right Quick Actions (Undo, Reset Round, Home) -->
        <div class="flex items-center justify-end gap-2.5 min-w-[200px] sm:min-w-[240px]">
            <!-- Undo Button -->
            <button
                class="h-11 w-11 inline-flex items-center justify-center rounded-xl bg-pink-50 hover:bg-pink-100 text-rose-600 transition-colors border border-rose-200/50 cursor-pointer active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
                title="Undo last score"
                type="button"
                on:click={undo}
                disabled={history.length === 0}
            >
                <span class="material-symbols-outlined text-[20px]">undo</span>
            </button>
            <!-- New Round Button -->
            <button
                class="h-11 inline-flex items-center gap-2 bg-[#6434da] hover:bg-[#5229b5] text-white text-sm font-semibold px-4 rounded-xl transition-colors shadow-sm cursor-pointer active:scale-95"
                type="button"
                on:click={reset}
            >
                <span class="material-symbols-outlined text-[18px]">restart_alt</span>
                <span class="hidden sm:inline">New round</span>
            </button>
            <!-- Exit to Setup / Lobby -->
            <button
                class="h-11 w-11 inline-flex items-center justify-center rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors border border-slate-200 cursor-pointer active:scale-95"
                title="Exit to Setup"
                type="button"
                on:click={newGame}
            >
                <span class="material-symbols-outlined text-[20px]">home</span>
            </button>
        </div>
    </header>
    <!-- END: TopControlsBar -->

    <!-- BEGIN: Hero Targets Display -->
    <section class="w-full max-w-[1600px] mx-auto mb-4 sm:mb-6 flex items-center justify-center">
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 w-full max-w-4xl">
            <!-- Higher Than Target Card -->
            <div class="flex items-center justify-between p-6 sm:p-8 rounded-3xl bg-white border-3 border-emerald-400 shadow-lg">
                <div class="flex items-center gap-4 sm:gap-5">
                    <div class="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-emerald-100 text-emerald-800 flex items-center justify-center shrink-0 shadow-inner">
                        <span class="material-symbols-outlined text-[36px] sm:text-[44px]">arrow_upward</span>
                    </div>
                    <div class="flex flex-col text-left">
                        <span class="text-sm sm:text-base md:text-lg font-black uppercase tracking-wider text-emerald-800 font-headline">Higher than</span>
                        <span class="text-xs sm:text-sm text-slate-500 font-medium">Target to beat</span>
                    </div>
                </div>
                <div class="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black text-[#005d46] font-headline tracking-tight">
                    {highTarget}
                </div>
            </div>

            <!-- Lower Than Target Card -->
            <div class="flex items-center justify-between p-6 sm:p-8 rounded-3xl bg-white border-3 border-indigo-400 shadow-lg">
                <div class="flex items-center gap-4 sm:gap-5">
                    <div class="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-indigo-100 text-indigo-800 flex items-center justify-center shrink-0 shadow-inner">
                        <span class="material-symbols-outlined text-[36px] sm:text-[44px]">arrow_downward</span>
                    </div>
                    <div class="flex flex-col text-left">
                        <span class="text-sm sm:text-base md:text-lg font-black uppercase tracking-wider text-indigo-800 font-headline">Lower than</span>
                        <span class="text-xs sm:text-sm text-slate-500 font-medium">Target to go under</span>
                    </div>
                </div>
                <div class="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black text-[#6434da] font-headline tracking-tight">
                    {lowTarget}
                </div>
            </div>
        </div>
    </section>
    <!-- END: Hero Targets Display -->

    <!-- BEGIN: MainScoreboardTable -->
    <main class="w-full max-w-[1600px] mx-auto flex flex-col">
        <div class="bg-white rounded-2xl sm:rounded-3xl shadow-md border border-black/5 overflow-hidden flex flex-col">
            <div class="overflow-auto">
                <table class="w-full text-center border-collapse">
                    <thead>
                        <!-- Primary Player Header Row -->
                        <tr class="h-12 sm:h-14 text-white text-lg">
                            <!-- Game Mode Header -->
                            <th class="w-[20%] min-w-[160px] bg-[#005d46] px-5 py-2.5 align-middle text-left font-bold text-lg sm:text-xl tracking-tight border-r border-emerald-900/30 text-white" scope="col" style="text-align: left;">
                                <div class="flex items-center justify-start gap-2 sm:gap-3 text-left">
                                    <span class="material-symbols-outlined text-[#21e467] text-[24px] sm:text-[28px] shrink-0">swap_vert</span>
                                    <div class="text-left">
                                        <div class="text-base sm:text-lg md:text-xl font-black font-headline tracking-tight text-white leading-tight">
                                            High Low
                                        </div>
                                        <div class="text-[11px] sm:text-xs text-emerald-200/80 font-medium">
                                            Score higher or lower
                                        </div>
                                    </div>
                                </div>
                            </th>

                            <!-- Player Headers -->
                            {#each $highLowPlayers as player}
                                <th
                                    class="px-4 py-2.5 align-middle font-bold border-r transition-colors {
                                        isPlayerWinner(player)
                                            ? 'bg-[#21e467] text-[#005d46] border-emerald-600/40 shadow-sm'
                                            : isPlayerActive(player)
                                            ? 'bg-[#fff8e7] text-amber-950 border-amber-200/80 shadow-inner'
                                            : 'bg-[#005d46] text-white border-emerald-900/30'
                                    }"
                                    scope="col"
                                    style="width: {80 / $highLowPlayers.length}%"
                                >
                                    <div class="flex items-center justify-center gap-2 flex-wrap">
                                        {#if isPlayerActive(player) && !isPlayerWinner(player)}
                                            <span class="inline-block w-2.5 h-2.5 rounded-full bg-[#21e467] border-2 border-emerald-700 animate-pulse"></span>
                                        {/if}
                                        <span class="text-xl sm:text-2xl font-bold font-headline {player.eliminated ? 'line-through opacity-60 text-rose-300' : ''}">
                                            {player?.name}
                                        </span>
                                        {#if isPlayerWinner(player)}
                                            <span class="text-xl sm:text-2xl leading-none select-none inline-flex items-center">
                                                🏆
                                            </span>
                                        {/if}
                                    </div>
                                </th>
                            {/each}
                        </tr>
                    </thead>

                    <tbody class="divide-y divide-slate-200/70 text-base font-medium">
                        <!-- ROW: Lives / Levens -->
                        <tr class="h-12 sm:h-14 transition-colors">
                            <td class="w-[20%] min-w-[160px] bg-stone-50/90 text-right pr-6 py-2 align-middle font-bold text-slate-500 border-r border-slate-200 text-xs sm:text-sm uppercase tracking-wider font-headline">
                                Lives
                            </td>
                            {#each $highLowPlayers as player}
                                <td class="py-2 align-middle border-r border-slate-200/50 {isPlayerActive(player) ? 'bg-[#fff8e7]/70 border-r border-amber-200/50' : ''}">
                                    <div class="flex items-center justify-center gap-1.5 flex-wrap px-2 min-h-[32px]">
                                        {#if player.lives > 0}
                                            {#each Array(player.lives) as _}
                                                <svg class="w-4 h-4 sm:w-5 sm:h-5 fill-red-600 text-red-600 transition-transform hover:scale-110 shrink-0" style="fill: #dc2626;" viewBox="0 0 24 24">
                                                    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"></path>
                                                </svg>
                                            {/each}
                                        {:else}
                                            <span class="text-xs font-bold text-rose-500 bg-rose-50 px-2 py-0.5 rounded-full border border-rose-200">ELIMINATED</span>
                                        {/if}
                                    </div>
                                </td>
                            {/each}
                        </tr>

                        <!-- ROW: Last Score / Result -->
                        <tr class="h-12 sm:h-14 transition-colors bg-stone-50/30">
                            <td class="w-[20%] min-w-[160px] bg-stone-50/90 text-right pr-6 py-2 align-middle font-bold text-slate-500 border-r border-slate-200 text-xs sm:text-sm uppercase tracking-wider font-headline">
                                Last score
                            </td>
                            {#each $highLowPlayers as player}
                                <td class="py-2 align-middle border-r border-slate-200/50 {isPlayerActive(player) ? 'bg-[#fff8e7]/70 border-r border-amber-200/50' : ''}">
                                    <div class="flex items-center justify-center gap-2 min-h-[32px]">
                                        {#if player.lastResult === 'high'}
                                            <div class="inline-flex items-center justify-center gap-1 text-emerald-700 font-bold bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-lg">
                                                <span class="text-lg sm:text-xl font-black font-headline">{player.lastScore}</span>
                                                <span class="material-symbols-outlined text-[20px] sm:text-[24px] text-emerald-600">arrow_upward</span>
                                            </div>
                                        {:else if player.lastResult === 'low'}
                                            <div class="inline-flex items-center justify-center gap-1 text-indigo-700 font-bold bg-indigo-50 border border-indigo-200 px-2.5 py-1 rounded-lg">
                                                <span class="text-lg sm:text-xl font-black font-headline">{player.lastScore}</span>
                                                <span class="material-symbols-outlined text-[20px] sm:text-[24px] text-indigo-600">arrow_downward</span>
                                            </div>
                                        {:else if player.lastResult === 'miss'}
                                            <div class="inline-flex items-center justify-center gap-1 text-rose-600 font-bold bg-rose-50 border border-rose-200 px-2.5 py-1 rounded-lg">
                                                {#if player.lastScore != null}
                                                    <span class="text-lg sm:text-xl font-black font-headline">{player.lastScore}</span>
                                                {/if}
                                                <span class="material-symbols-outlined text-[20px] sm:text-[24px] text-rose-500">cancel</span>
                                            </div>
                                        {:else}
                                            <span class="text-slate-300 font-bold text-lg select-none">-</span>
                                        {/if}
                                    </div>
                                </td>
                            {/each}
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Footer Info Strip inside table card -->
            <div class="px-5 sm:px-6 py-2.5 bg-stone-50 border-t border-slate-200/80 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-500 font-medium shrink-0">
                <div class="flex items-center gap-3 sm:gap-4">
                    <span class="inline-flex items-center gap-1.5">
                        <span class="w-2 h-2 rounded-full bg-[#21e467]"></span>
                        <strong class="text-slate-700">High Low rules:</strong> Score higher than {highTarget} or lower than {lowTarget} to stay in the game
                    </span>
                    <span class="text-slate-300 hidden sm:inline">|</span>
                    <span class="hidden sm:inline">Starting lives: {lives}</span>
                </div>
                <div class="flex items-center gap-3">
                    <span class="text-slate-400">Press <kbd class="px-1.5 py-0.5 bg-white border border-slate-300 rounded text-[11px] text-slate-700 font-mono shadow-xs font-semibold">ENTER</kbd> to submit score</span>
                </div>
            </div>
        </div>
    </main>
    <!-- END: MainScoreboardTable -->
</div>

<style>
    .highlow-screen {
        font-family: 'Space Grotesk', 'Inter', sans-serif;
    }

    .highlow-screen table {
        table-layout: fixed;
    }

    .highlow-screen table th,
    .highlow-screen table td {
        vertical-align: middle !important;
    }

    input[type=number]::-webkit-inner-spin-button,
    input[type=number]::-webkit-outer-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    input[type=number] {
        -moz-appearance: textfield;
    }
</style>
